// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import FormDialog from '../../../components/Dialog/FormDialog';
import useMutate from '../../../hooks/mutations/useMutate';
import useBridgeAgreementOffer from '../../../hooks/queries/bridge/agreement/useBridgeAgreementOffer';
import useBridgeServiceRequest from '../../../hooks/queries/bridge/agreement/useBridgeServiceRequest';
import TextInput from '../../../components/Form/TextInput';
import useAcceptBridgeService from '../../../hooks/mutations/bridge/onboarding/useAcceptBridgeService';
import useAcceptBridgeAgreement from '../../../hooks/mutations/bridge/onboarding/useAcceptBridgeAgreement';
import { ERR_ACC_BRIDGE_AGREEMENT, ERR_ACC_BRIDGE_SERVICE, SUC_ACC_BRIDGE_AGREEMENT, SUC_ACC_BRIDGE_SERVICE } from '../../../utils/strings';

export type DialogProps = {
  open: boolean;
  onClose: () => void;

};

export const AcceptBridgeDialog = ({ open, onClose }: DialogProps) => {
  const mutate = useMutate();
  const [withdrawalAttester, setWithdrawalAttester] = useState<string>('');
  const [maxNonceMapSize, setMaxNonceMapSize] = useState<string>('');
  const [minBurnValue, setMinBurnValue] = useState<string>('');

  const bridgeAgreementOffer = useBridgeAgreementOffer();
  const bridgeServiceRequest = useBridgeServiceRequest();
  const acceptBridgeService = useAcceptBridgeService();
  const acceptBridgeAgreement = useAcceptBridgeAgreement();

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    const agreementPayload = {
      bridgeAgreementOfferCid: bridgeAgreementOffer.data!.contractId,
      nonceConfig: {
        maxNonceMapSize
      },
      bridgeConfig: {
        minBurnValue
      },
      withdrawalAttester
    };
    const agreemmentRes = await mutate(acceptBridgeAgreement, agreementPayload, SUC_ACC_BRIDGE_AGREEMENT, ERR_ACC_BRIDGE_AGREEMENT);
    const payload = {
      bridgeServiceRequestCid: bridgeServiceRequest.data!.contractId
    };
    const res = await mutate(acceptBridgeService, payload, SUC_ACC_BRIDGE_SERVICE, ERR_ACC_BRIDGE_SERVICE);
    handleClose();
    return ({res, agreemmentRes});
  }, [acceptBridgeService, handleClose, mutate]);

  const isLoading = false;
  if (isLoading) return null;

  return (

    <FormDialog
      open={open}
      title="Initiate the Bridge"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Initiate"
    >
      <TextInput
        label="Withdrawal Attester"
        value={withdrawalAttester}
        setValue={setWithdrawalAttester}
        required
      />
      <TextInput
        type="number"
        label="Max Nonce Size"
        value={maxNonceMapSize}
        setValue={setMaxNonceMapSize}
        required
      />
      <TextInput
        type="number"
        label="Min Burn Amount"
        value={minBurnValue}
        setValue={setMinBurnValue}
        required
      />
    </FormDialog>
  );
};
